import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import { useAdvancedFiltersUrl } from '@/hooks/useAdvancedFiltersUrl';
import { useGeometry } from '@/hooks/useGeometry';
import { AppRouters } from '@/utils/constants/routers';
import type { DynamicFilter } from '@/context/geometry';
import {
  applyDimensionLayerToFilters,
  getEffectiveDimensionLayerFromUrlAndState,
} from '@/utils/functions/advancedFilters';

export interface UseTerritorialDashboardMapFiltersOptions {
  variant: 'risk' | 'opportunity';
  selectedBigqueryId: string;
  selectedLayer: number;
  setSelectedLayerState: Dispatch<SetStateAction<number>>;
  isMunicipality: boolean;
  currentLanguage?: 'en-US' | 'pt-BR';
  setLoadingData: (loading: boolean) => void;
  isInitialMountRef: RefObject<boolean>;
}

export function useTerritorialDashboardMapFilters({
  variant,
  selectedBigqueryId,
  selectedLayer,
  setSelectedLayerState,
  isMunicipality,
  currentLanguage,
  setLoadingData,
  isInitialMountRef,
}: UseTerritorialDashboardMapFiltersOptions) {
  const { getFiltersFromUrl, setFiltersToUrl } = useAdvancedFiltersUrl();
  const { getGeometry, getGeometryForMap } = useGeometry();

  const getGeometryRef = useRef(getGeometry);
  const getGeometryForMapRef = useRef(getGeometryForMap);
  getGeometryRef.current = getGeometry;
  getGeometryForMapRef.current = getGeometryForMap;

  const floatingButtonsVariant = useMemo(() => {
    if (selectedBigqueryId === 'economica') {
      return 'opportunity' as const;
    }
    if (selectedBigqueryId === 'seguranca') {
      return 'risk' as const;
    }
    return variant;
  }, [selectedBigqueryId, variant]);

  const handleLayerChange = useCallback(
    (layer: number) => {
      const currentFilters = getFiltersFromUrl();
      const updatedFilters = applyDimensionLayerToFilters(currentFilters, layer);
      setFiltersToUrl(AppRouters.home, updatedFilters);
    },
    [getFiltersFromUrl, setFiltersToUrl]
  );

  const refetchGeometryForMapState = useCallback(async () => {
    const urlFilters = getFiltersFromUrl();

    const effectiveDimensionLayer = getEffectiveDimensionLayerFromUrlAndState(
      urlFilters,
      selectedLayer
    );

    const hasAdditionalMunicipalityCodes =
      (urlFilters.additionalMunicipalityCodes || []).length > 0;

    if (effectiveDimensionLayer === -10 && isMunicipality && !hasAdditionalMunicipalityCodes) {
      return;
    }

    let dynamicFilters: DynamicFilter[] = [];

    if (effectiveDimensionLayer !== -10) {
      dynamicFilters = [
        {
          field: 'dimension_id',
          operator: 'eq',
          value: effectiveDimensionLayer,
        },
      ];
    }

    const otherDynamicFilters = (urlFilters.dynamicFilters || []).filter(
      (f) => f.field !== 'dimension_id' && f.field !== 'municipality_code'
    );

    try {
      setLoadingData(true);

      if (isMunicipality && !hasAdditionalMunicipalityCodes) {
        const municipalityFilter = urlFilters.dynamicFilters?.find(
          (f) => f.field === 'municipality_code'
        );

        const allDynamicFilters = municipalityFilter
          ? [...dynamicFilters, municipalityFilter]
          : dynamicFilters;

        await getGeometryRef.current({
          filters: {
            dynamicFilters: allDynamicFilters,
            dimensionLevelFilters: urlFilters.dimensionLevelFilters,
          },
          pagination: { limit: 1 },
          language: currentLanguage,
        });
      } else if (hasAdditionalMunicipalityCodes) {
        await getGeometryRef.current({
          filters: {
            dynamicFilters: [...dynamicFilters, ...otherDynamicFilters],
            dimensionLevelFilters: urlFilters.dimensionLevelFilters,
            additionalMunicipalityCodes: urlFilters.additionalMunicipalityCodes,
          },
          pagination: { limit: 5600 },
          language: currentLanguage,
        });
      } else {
        const channel =
          selectedBigqueryId === 'overall' || !selectedBigqueryId ? 'general' : selectedBigqueryId;

        await getGeometryForMapRef.current({
          filters: {
            type: floatingButtonsVariant,
            channel,
            dynamicFilters: [...dynamicFilters, ...otherDynamicFilters],
            dimensionLevelFilters: urlFilters.dimensionLevelFilters,
          },
          language: currentLanguage,
        });
      }
    } finally {
      setLoadingData(false);
    }
  }, [
    currentLanguage,
    floatingButtonsVariant,
    getFiltersFromUrl,
    isMunicipality,
    selectedBigqueryId,
    selectedLayer,
    setLoadingData,
  ]);

  useEffect(() => {
    if (isInitialMountRef.current) {
      return;
    }

    const urlFilters = getFiltersFromUrl();
    const dimensionIdFromUrl = urlFilters.dynamicFilters?.find((f) => f.field === 'dimension_id')
      ?.value as number | undefined;

    const targetLayer = dimensionIdFromUrl ?? -10;

    setSelectedLayerState((currentLayer) => {
      if (targetLayer !== currentLayer) {
        return targetLayer;
      }
      return currentLayer;
    });
  }, [getFiltersFromUrl, isInitialMountRef, setSelectedLayerState]);

  return { handleLayerChange, refetchGeometryForMapState, floatingButtonsVariant };
}
